import type { ReactNode } from 'react'
import { HugeiconsIcon } from '@hugeicons/react'
import { Search01Icon } from '@hugeicons/core-free-icons'
import { cn } from '@/lib/utils'

/**
 * Analytics detail-tab building blocks (White signal v2, Paper "App v3 ·
 * Analytics Pages · LP-aligned" and "App v3 · Analytics Sources · LP-aligned").
 * A verdict line with one orange answer, then a `.well` of raised cards: a KPI
 * tile row, sentence-case labels, neutral deltas.
 */

/** Verdict: 22px headline with the view's one answer chip, plus a muted meta line. */
export function ReportVerdict({ children, meta }: { children: ReactNode; meta?: ReactNode }) {
  return (
    <div className="flex flex-col gap-1 py-1">
      <h2 className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[22px] font-semibold leading-7 tracking-[-0.02em] text-foreground">
        {children}
      </h2>
      {meta && <p className="text-[13px] leading-4 text-muted-foreground">{meta}</p>}
    </div>
  )
}

/** The answer: orange-strong text on the primary tint with its ring. One per view. */
export function AnswerChip({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex h-7 items-center rounded-md bg-primary-tint px-2 text-primary-strong tabular-nums ring-1 ring-primary-tint-ring">
      {children}
    </span>
  )
}

export function KpiStrip({ children }: { children: ReactNode }) {
  return <div className="flex overflow-x-auto scrollbar-hide border-b border-border/60">{children}</div>
}

/**
 * Previous-period delta: up reads in ink, down in muted. No arrows, no green
 * (White signal v2). Renders nothing for null / 0 / non-finite, so callers
 * pass null while the comparison loads or fails.
 */
export function KpiDelta({
  value,
  unit = '%',
  title,
}: {
  value: number | null | undefined
  unit?: '%' | ' pts'
  title?: string
}) {
  if (value == null || value === 0 || !Number.isFinite(value)) return null
  return (
    <span
      title={title}
      className={cn(
        'shrink-0 text-xs font-medium leading-4 tabular-nums',
        value > 0 ? 'text-foreground' : 'text-muted-foreground',
      )}
    >
      {value > 0 ? '+' : '\u2212'}
      {Math.abs(value)}
      {unit}
    </span>
  )
}

/**
 * KPI tile. `selected` marks the series in focus on the card's chart with a
 * 2px ink underline, exactly as the Overview strip does.
 * `delta` is a previous-period change (`usePreviousPeriodMetrics`).
 */
export function KpiTile({
  label,
  value,
  first,
  selected,
  title,
  delta,
  deltaUnit,
  deltaTitle,
}: {
  label: string
  value: ReactNode
  first?: boolean
  selected?: boolean
  title?: string
  delta?: number | null
  deltaUnit?: '%' | ' pts'
  deltaTitle?: string
}) {
  return (
    <div
      title={title}
      className={cn(
        'flex min-w-[140px] flex-1 basis-0 flex-col gap-1.5 px-[18px] py-4',
        !first && 'border-l border-border/60',
        // Ink, not orange: orange stays on the view's one CTA (White signal v2)
        selected && 'shadow-[inset_0_-2px_0_var(--foreground)]',
      )}
    >
      <span className="whitespace-nowrap text-xs leading-4 text-muted-foreground">{label}</span>
      <span className="flex min-w-0 items-baseline gap-1.5">
        <span className="truncate text-[22px] font-semibold leading-7 tracking-[-0.02em] text-foreground tabular-nums">
          {value}
        </span>
        <KpiDelta value={delta} unit={deltaUnit} title={deltaTitle} />
      </span>
    </div>
  )
}

export function LegendKey({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className="h-[3px] w-2.5 shrink-0 rounded-[2px]" style={{ backgroundColor: color }} />
      <span className="text-xs leading-4 text-muted-foreground">{label}</span>
    </span>
  )
}

/** Card search field: 30px, 8px radius, hairline ring. */
export function ReportSearch({
  value,
  onChange,
  placeholder,
  className,
}: {
  value: string
  onChange: (value: string) => void
  placeholder: string
  className?: string
}) {
  return (
    <label
      className={cn(
        'flex h-[30px] w-full items-center gap-2 rounded-lg px-2.5 ring-1 ring-foreground/[0.09] focus-within:ring-2 focus-within:ring-ring/40 sm:w-[220px]',
        className,
      )}
    >
      <HugeiconsIcon icon={Search01Icon} size={13} className="shrink-0 text-muted-foreground/70" />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="min-w-0 flex-1 bg-transparent text-xs leading-4 text-foreground outline-none placeholder:text-muted-foreground/80"
      />
    </label>
  )
}

/** Table footer: range on the left, Previous / Next on the right. */
export function ReportPagination({
  from,
  to,
  total,
  noun,
  onPrevious,
  onNext,
  previousDisabled,
  nextDisabled,
  busy,
  label,
}: {
  /** Replaces the "from to of total" range, e.g. "1 page matches the filter" */
  label?: ReactNode
  from: number
  to: number
  total: number
  noun: string
  onPrevious: () => void
  onNext: () => void
  previousDisabled?: boolean
  nextDisabled?: boolean
  busy?: boolean
}) {
  const btn =
    'flex h-7 items-center rounded-lg bg-card px-2.5 text-xs font-medium leading-4 text-foreground ring-1 ring-foreground/[0.09] transition-colors hover:bg-muted/60 disabled:pointer-events-none disabled:text-muted-foreground/60'
  return (
    <div className="flex h-12 items-center justify-between border-t border-border/60 px-[18px]">
      <p className="text-xs leading-4 text-muted-foreground tabular-nums">
        {label ?? <>{from} to {to} of {total} {noun}</>}
        {busy && <span className="ml-2">(loading…)</span>}
      </p>
      <div className="flex gap-1.5">
        <button type="button" className={btn} onClick={onPrevious} disabled={previousDisabled}>
          Previous
        </button>
        <button type="button" className={btn} onClick={onNext} disabled={nextDisabled}>
          Next
        </button>
      </div>
    </div>
  )
}

/** Share meter: 56px neutral track, pale orange fill. Solid orange is reserved for the answer. */
export function ShareMeter({ percent }: { percent: number }) {
  const pct = Math.max(0, Math.min(100, percent))
  return (
    <span className="flex items-center justify-end gap-2.5">
      <span className="flex h-1 w-14 shrink-0 overflow-hidden rounded-[2px] bg-muted">
        <span className="h-1 rounded-[2px] bg-primary-tint-ring" style={{ width: `${pct}%` }} />
      </span>
      <span className="w-9 shrink-0 text-right text-[13px] leading-4 text-muted-foreground tabular-nums">
        {pct < 1 && pct > 0 ? '<1' : Math.round(pct)}%
      </span>
    </span>
  )
}

/** Table class recipe shared by the report tables. */
export const reportTable = {
  headRow: 'border-y border-border/60 bg-muted/60 hover:bg-muted/60',
  th: 'h-8 px-3 first:pl-[18px] last:pr-[18px] text-xs font-normal leading-4 text-muted-foreground whitespace-nowrap',
  row: 'border-b border-border/60 last:border-0 hover:bg-muted/40',
  td: 'h-[52px] px-3 first:pl-[18px] last:pr-[18px] text-[13px] leading-4',
  num: 'text-right font-medium text-foreground tabular-nums',
  numMuted: 'text-right text-muted-foreground tabular-nums',
  empty: 'py-12 text-center text-[13px] text-muted-foreground',
}
